<script lang="ts" setup>
import {
	breakpointsTailwind,
	useIntersectionObserver,
	useWindowScroll
} from '@vueuse/core';

import propertyPricesLocale from '../Property/PropertyPrices/PropertyPrices.locale.json';

const props = withDefaults(
	defineProps<{
		firstName?: string;
		displayAfter?: HTMLElement | null;
		hideAfter?: HTMLElement | null;
		hideOffset?: number;
		show?: boolean;
	}>(),
	{ show: true, firstName: '' }
);
defineEmits(['click']);

const isVisible = ref(false);
const { y: scrollY } = useWindowScroll();
const { isSmallerOrEqual } = useBreakpoints(breakpointsTailwind);
const strings = propertyPricesLocale.pt;
const elementPosition = ref(0);
const hidePosition = ref(0);

const { stop: stopDisplay } = useIntersectionObserver(
	() => props.displayAfter,
	([entry]) => {
		if (!entry?.target) return;

		const rect = entry.target.getBoundingClientRect();

		elementPosition.value = rect.bottom + window.scrollY - 115;
	},
	{
		threshold: [0, 0.25, 0.5, 0.75, 1],
		rootMargin: '-100px 0px 0px 0px'
	}
);

const { stop: stopHide } = useIntersectionObserver(
	() => props.hideAfter,
	([entry]) => {
		if (!entry?.target) return;

		const rect = entry.target.getBoundingClientRect();

		hidePosition.value = rect.top + window.scrollY - (props.hideOffset || 115);
	},
	{
		threshold: [0, 0.25, 0.5, 0.75, 1],
		rootMargin: '-100px 0px 0px 0px'
	}
);

const shouldShow = computed(() => {
	if (!isSmallerOrEqual('lg')) return false;

	if (!props.displayAfter) return true;

	if (props.hideAfter && scrollY.value >= hidePosition.value) return false;

	return isVisible.value;
});

watchEffect(() => {
	if (!elementPosition.value) return;

	isVisible.value = scrollY.value > elementPosition.value;
});

onUnmounted(() => {
	stopDisplay();
	stopHide();
});
</script>

<template>
	<ClientOnly>
		<Transition
			enter-active-class="transition-all duration-300 ease-out"
			enter-from-class="opacity-0 translate-y-full"
			enter-to-class="opacity-100 translate-y-0"
			leave-active-class="transition-all duration-300 ease-in"
			leave-from-class="opacity-100 translate-y-0"
			leave-to-class="opacity-0 translate-y-full"
		>
			<div
				v-if="shouldShow && show"
				class="fixed bottom-6 left-1/2 z-[100] -translate-x-1/2"
			>
				<slot>
					<Button
						variant="primary"
						size="sm"
						class="size-fit font-medium"
						@click="$emit('click')"
					>
						{{ strings.broker.button }} {{ firstName }}
					</Button>
				</slot>
			</div>
		</Transition>
	</ClientOnly>
</template>
